import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";

function detailMessage(err, fallback) {
  const d = err?.response?.data?.detail;
  if (d && typeof d === "object" && d.message) return d.message;
  return friendlyError(err, fallback);
}

function confirmButtonLabel(busy, flow) {
  if (busy) return <Loader2 className="w-4 h-4 animate-spin" />;
  return flow === "enroll" ? "Confirm & turn on" : "Confirm & turn off";
}

export default function AccountSecurityPage() {
  const [status, setStatus] = useState(null);
  const [flow, setFlow] = useState(null); // null | "enroll" | "disable"
  const [step, setStep] = useState("password"); // "password" | "code"
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = () =>
    api.get("/auth/2fa/status").then(({ data }) => setStatus(data)).catch(() => setStatus({ enabled: false }));

  useEffect(() => { loadStatus(); }, []);

  const reset = () => { setFlow(null); setStep("password"); setPassword(""); setCode(""); setChallenge(null); };

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = flow === "enroll" ? "/auth/2fa/enable" : "/auth/2fa/request-disable";
      const { data } = await api.post(url, { password });
      setChallenge(data);
      setStep("code");
      toast.info(`We emailed a 6-digit code to ${data.email_hint}.`);
    } catch (err) {
      toast.error(detailMessage(err, "Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = flow === "enroll" ? "/auth/2fa/confirm" : "/auth/2fa/disable";
      await api.post(url, { challenge_id: challenge.challenge_id, code: code.trim() });
      toast.success(flow === "enroll"
        ? "Two-factor authentication is ON. You'll enter an email code at every sign-in."
        : "Two-factor authentication is OFF.");
      reset();
      await loadStatus();
    } catch (err) {
      toast.error(detailMessage(err, "Verification failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
        <h1 className="font-display text-3xl font-extrabold mb-2" style={{ color: "var(--text-primary)" }}>
          Account security
        </h1>
        <p className="text-sm mb-8" style={{ color: "var(--text-secondary)" }}>
          Protect the personal information in your immigration case.
        </p>

        <div className="card-surface p-6" data-testid="twofa-settings-card">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {status?.enabled
                  ? <ShieldCheck className="w-5 h-5" style={{ color: "#4B6A4F" }} />
                  : <ShieldOff className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />}
                <h2 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
                  Two-factor authentication
                </h2>
              </div>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                {status === null
                  ? "Loading…"
                  : status.enabled
                    ? "ON — every sign-in requires a 6-digit code emailed to you."
                    : "OFF — your account is protected by password only. Turn this on for a second layer of security."}
              </p>
            </div>
            {status !== null && !flow && (
              <button
                type="button"
                className={status.enabled ? "btn-ghost text-sm" : "btn-primary text-sm"}
                style={status.enabled ? { border: "1px solid var(--border-stone)" } : undefined}
                onClick={() => { setFlow(status.enabled ? "disable" : "enroll"); setStep("password"); }}
                data-testid="twofa-toggle-btn"
              >
                {status.enabled ? "Turn off" : "Turn on"}
              </button>
            )}
          </div>

          {flow && (
            <div className="mt-5 border-t pt-5" style={{ borderColor: "var(--border-stone)" }}>
              {step === "password" ? (
                <form onSubmit={requestCode} className="space-y-3" data-testid="twofa-password-form">
                  <label className="field-label">
                    Confirm your password to {flow === "enroll" ? "enable" : "disable"} 2FA
                  </label>
                  <input type="password" className="field-input" value={password}
                    onChange={(e) => setPassword(e.target.value)} autoFocus
                    data-testid="twofa-password-input" />
                  <div className="flex gap-2 justify-end">
                    <button type="button" className="btn-ghost text-sm" onClick={reset} data-testid="twofa-flow-cancel">Cancel</button>
                    <button type="submit" className="btn-primary text-sm" disabled={busy || !password}
                      data-testid="twofa-send-code-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Email me a code"}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={submitCode} className="space-y-3" data-testid="twofa-code-form">
                  <label className="field-label">Enter the 6-digit code we emailed you</label>
                  <input className="field-input text-center text-2xl tracking-[0.5em] font-mono"
                    inputMode="numeric" maxLength={6} autoComplete="one-time-code" value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus
                    data-testid="twofa-settings-code-input" />
                  <div className="flex gap-2 justify-end">
                    <button type="button" className="btn-ghost text-sm" onClick={reset} data-testid="twofa-code-cancel">Cancel</button>
                    <button type="submit" className="btn-primary text-sm" disabled={busy || code.length !== 6}
                      data-testid="twofa-confirm-btn">
                      {confirmButtonLabel(busy, flow)}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
